import React, {
  createContext,
  PropsWithChildren,
  useEffect,
  useState,
} from "react";
import {
  ListViewSpecificPreferenceKey,
  ListViewSpecificValues,
  PreferenceGroup,
} from "@services/userPreferences/types";
import { useErrorNotification } from "../Notification";
import {
  createUserPreference,
  getAllUserPreferences,
  updateUserPreference,
} from "@services/userPreferences";
import {
  defaultSpecificValues,
  preferencesDefaultContextValue,
} from "./defaults";

export type SpecificPreferencesContextType = ListViewSpecificValues & {
  loaded: boolean;
  errored: boolean;
  assetKey: ListViewSpecificPreferenceKey;
};

export type SpecificPreferencesDispatchContextType = {
  update: <D>(data: Partial<D>) => Promise<void>;
  updateWidth: (key: PropertyKey, width: number) => Promise<void>;
};

export const SpecificPreferencesContext =
  createContext<SpecificPreferencesContextType>(preferencesDefaultContextValue);

export const SpecificPreferencesDispatchContext =
  createContext<SpecificPreferencesDispatchContextType>({
    update: async () => {},
    updateWidth: async () => {},
  });

const GROUP_NAME = PreferenceGroup.LIST_VIEW;
export const SpecificPreferencesProvider = ({
  assetKey,
  children,
}: PropsWithChildren<{
  assetKey: ListViewSpecificPreferenceKey;
}>) => {
  const dispatchError = useErrorNotification();
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [data, setData] = useState<ListViewSpecificValues>(
    defaultSpecificValues
  );

  const load = async () => {
    let mustCreate: boolean = false;
    const key = assetKey;
    try {
      const preferences = await getAllUserPreferences();
      const data = preferences.find(
        (x) => x.preferenceGroup === GROUP_NAME && x.key === key
      );
      // FIXME: get rid of any
      if (data) setData({ ...defaultSpecificValues, ...(data.value as any) });
      else mustCreate = true;
    } catch (e) {
      dispatchError(e);
      setErrored(true);
    } finally {
      setLoaded(true);
    }
    if (mustCreate) {
      try {
        /**
         * To make it much simpler when editing, just create the preference that doesn't exist yet in BE
         */
        createUserPreference(GROUP_NAME, {
          preferenceGroup: GROUP_NAME,
          key,
          value: defaultSpecificValues,
        });
      } catch (e) {
        dispatchError(e);
      }
    }
  };

  const update: SpecificPreferencesDispatchContextType["update"] = async (
    newData
  ) => {
    // optimistic update
    setData((prevData) => ({ ...prevData, ...newData }));
    try {
      await updateUserPreference(GROUP_NAME, assetKey, {
        preferenceGroup: GROUP_NAME,
        key: assetKey,
        value: { ...data, ...newData },
      });
      load();
    } catch (e) {
      dispatchError(e);
    }
  };

  const updateWidth: SpecificPreferencesDispatchContextType["updateWidth"] =
    async (key, width) => {
      try {
        await updateUserPreference(GROUP_NAME, assetKey, {
          preferenceGroup: GROUP_NAME,
          key: assetKey,
          value: {
            ...data,
            columnWidths: { ...data.columnWidths, [key]: width },
          },
        });
        load();
      } catch (e) {
        dispatchError(e);
      }
    };
  useEffect(() => {
    load();
  }, []);

  return (
    <SpecificPreferencesContext.Provider
      value={{ loaded, errored, assetKey, ...data }}
    >
      <SpecificPreferencesDispatchContext.Provider
        value={{ update, updateWidth }}
      >
        {loaded && children}
      </SpecificPreferencesDispatchContext.Provider>
    </SpecificPreferencesContext.Provider>
  );
};
